<template>
  <v-menu>
    <template #activator="{ props: menuProps }">
      <v-tooltip location="top" transition="fade-transition">
        <template #activator="{ props: tooltipProps }">
          <app-button v-if="multiFiles" type="ghost" variant="neutral" size="medium" v-bind="{ ...menuProps, ...tooltipProps }" class="btn-ellipsis">
            <app-material-icon name="ellipsis" size="xs" type="fa" />
            <span>{{t('moreActions')}}</span>
          </app-button>
          <app-button v-else type="icon" v-bind="{ ...menuProps, ...tooltipProps }" class="btn-ellipsis" size="small" data-locator="other-actions-menu-button">
            <app-material-icon name="ellipsis" size="xs" type="fa" />
          </app-button>
        </template>
        <span>{{ $t('common.additionalActions') }}</span>
      </v-tooltip>
    </template>

    <v-list data-locator="checklist-attachment-menu">
      <OfficeOnlineEditionButton v-if="onlyOneFile && isOfficeEditionButtonShowed(file)" :document="file" :btnHasIconAndText="true"/>

      <app-dropdown-button-item
        v-if="onlyOneFile"
        data-locator="soverview-menu-item"
        icon="eye"
        :label="$t('project.checklist.table.ChecklistTableTaskAttachedFilesItemMenu.preview')"
        @click="onOpenDocument"
      />
      <template v-if="!globalDialog">
        <AppDownloadFile v-if="onlyOneFile && !actionBar" :link="fileLink(file.id)" class="d-flex w-100">
          <template #default="{ submitForm }">
            <app-dropdown-button-item
              class="w-100"
              data-locator="download-menu-item"
              icon="arrow-down-to-line"
              :label="$t('common.download')"
              @click="submitForm"
            />
          </template>
        </AppDownloadFile>
        <app-dropdown-button-item
          v-else
          data-locator="download-menu-item"
          icon="arrow-down-to-line"
          :label="$t('common.download')"
          @click="OPEN_DOCUMENTS_DOWNLOAD_FILES(handledFiles)"
        />
      </template>
      <app-dropdown-button-item
        v-if="isCurrentUserPm"
        data-locator="share-menu-item"
        icon="share"
        :label="$t('common.share')"
        @click="handleMenuAction('share')"
      />

      <app-dropdown-button-item
        v-if="versionManagementType && onlyOneFile"
        :data-locator="versionDataLocator(versionManagementType)"
        icon="clock-rotate-left"
        :label="t(versionManagementType)"
        @click="handleMenuAction(versionManagementType)"
      />

      <app-dropdown-button-item
        v-if="isCurrentUserPm && file.numbering && onlyOneFile"
        data-locator="modify-numbering-menu-item"
        icon="hashtag"
        :label="t('numbering')"
        @click="handleMenuAction('numbering')"
      />

      <app-dropdown-button-item
        v-if="onlyOneFile && file.canResume"
        data-locator="ia-summary-menu-item"
        :label="$t('project.documents.table.DocumentsTableItemMenu.summary')"
        tag="new"
        @click="handleMenuAction('summary')"
      >
        <template #icon>
          <v-img
            src="@/assets/img/ai_assistant_ico.svg"
            alt="AI assistant icon"
          />
        </template>
      </app-dropdown-button-item>

      <template v-if="file.canWrite">
        <v-divider class="mx-2 my-1" />
        <app-dropdown-button-item
          data-locator="move-menu-item"
          icon="arrow-turn-right"
          :label="t('move')"
          @click="handleMenuAction('move')"
        />
        <app-dropdown-button-item
          v-if="onlyOneFile"
          data-locator="duplicate-menu-item"
          icon="copy"
          :label="t('duplicate')"
          @click="handleMenuAction('duplicate')"
        />
        <app-dropdown-button-item
          data-locator="rename-menu-item"
          icon="pen"
          :label="t('rename')"
          @click="handleMenuAction('rename')"
        />
        <app-dropdown-button-item
          data-locator="delete-menu-item"
          :label="$t('common.delete')"
          @click="handleMenuAction('delete')"
          class="text-error"
        >
          <template #icon>
            <app-icon-ds icon="trash-can" size="xs" class="text-error" />
          </template>
        </app-dropdown-button-item>
        <v-divider class="mx-2 my-1" />
      </template>

      <app-dropdown-button-item
        v-if="enableAddToSigningChecklist"
        data-locator="add-to-signing-checklist-menu-item"
        icon="file-signature"
        :label="t('signingChecklistAdd')"
        @click="handleMenuAction('signingChecklistAdd')"
      />

      <ArchiveAddAccessListItem
        :routeOptions="archiveAddRouteOptions"
        :btnHasIconAndText="true"
        compact
        data-locator="archive-menu-item"
      />

      <PolyOfficeListItemExport
        v-if="polyOfficeEnabled"
        :fileSize="file.size"
        :btnHasIconAndText="true"
        @polyOfficeOpenDialog="polyOfficeOpenDialogHandler(file)"
        compact
      />

      <app-dropdown-button-item
        v-if="imanageEnabledAndLogged"
        data-locator="imanage-export-menu-item"
        :label="t('exportToImanage')"
        @click="isExportToImanageDialogOpen = true"
      >
        <template #icon>
          <img
            src="@/assets/img/icons/iManage.svg"
            alt="iManage icon"
          />
        </template>
      </app-dropdown-button-item>
    </v-list>
  </v-menu>

  <DocumentsNumberingDialog
    v-if="numberingDialogIsOpen"
    :document="file"
    @close="numberingDialogIsOpen = false"
    @onSuccess="updateDocumentsNumbering"
  />

  <IManageFolderPickerDialog
    v-if="isExportToImanageDialogOpen"
    v-model:is-open="isExportToImanageDialogOpen"
    @folders="exportFilesToImanage"
  />
</template>

<script>
import axios from 'axios'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import ArchiveAddAccessListItem from '@/common/archives/ArchiveAddAccessListItem.vue'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import OfficeOnlineEditionButton from '@/common/office/OfficeOnlineEditionButton.vue'
import { DocumentsExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeListItemExport from '@/common/polyOffice/PolyOfficeListItemExport.vue'
import { isFileSignable, openFileViewer } from '@/common/utils/files'
import { canFilesBeAddedToChecklist } from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import DocumentsNumberingDialog from '@/project/documents/dialogs/DocumentsNumberingDialog.vue'
import { SIGNING_CHECKLIST_ADD_ROUTE_NAME } from '@/router'
import { SET_CHECKLIST_VERSIONING_PANE } from '@/store/modules/checklist/action_types'
import { SET_RENAME_ATTACHED_FILE_DIALOG } from '@/store/modules/checklist/mutation_types'
import { SET_TABLE_DOCUMENTS_PANE } from '@/store/modules/documents/action_types'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { OPEN_DOCUMENTS_DOWNLOAD_FILES } from '@/store/modules/documents-download-files/action_types'
import { POST_FILDERS_TO_IMANAGE } from '@/store/modules/imanage/action_types'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'

export default {
  name: 'ChecklistTableTaskAttachedFilesItemMenu',
  components: {
    AppDownloadFile,
    IManageFolderPickerDialog,
    ArchiveAddAccessListItem,
    DocumentsNumberingDialog,
    OfficeOnlineEditionButton,
    PolyOfficeListItemExport,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    file: {
      type: Object,
      required: true,
    },
    multiFiles: {
      type: Array,
      required: false,
    },
    globalDialog: {
      type: Boolean,
      default: false,
    },
    actionBar: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    return useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableTaskAttachedFilesItemMenu' })
  },
  data () {
    return {
      isExportToImanageDialogOpen: false,
      numberingDialogIsOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['currentUserRights', 'isCurrentUserPm', 'maxSignableSize', 'isWopiEnabled']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    handledFiles () {
      if (this.multiFiles && Array.isArray(this.multiFiles) && this.multiFiles.length) {
        return this.multiFiles
      }
      return this.file ? [this.file] : []
    },
    handledFilesIds () {
      return this.handledFiles.map(file => file.id).join(',')
    },
    onlyOneFile () {
      return this.handledFiles.length === 1
    },
    archiveAddRouteOptions () {
      return {
        state: {
          files: this.handledFiles,
        },
        query: {
          fileIds: this.handledFilesIds,
          fromMnemo: this.mnemo,
        },
      }
    },
    isFileSignableComputed () {
      return isFileSignable(this.file, this.maxSignableSize)
    },
    canFilesBeAddedToChecklist () {
      return canFilesBeAddedToChecklist(this.handledFiles, this.isCurrentUserPm, this.maxSignableSize)
    },
    enableAddToSigningChecklist () {
      if (this.onlyOneFile) {
        return this.isCurrentUserPm && this.isFileSignableComputed
      } else {
        return this.canFilesBeAddedToChecklist
      }
    },
    versionManagementType () {
      if (this.isCurrentUserPm || this.currentUserRights?.canUploadVersions) {
        return 'manageVersions'
      } else if (this.file.nbVersions > 1) {
        return 'showVersions'
      } else {
        return null
      }
    },
  },
  methods: {
    ...mapActions('checklist', [SET_CHECKLIST_VERSIONING_PANE]),
    ...mapActions('documents', [SET_TABLE_DOCUMENTS_PANE]),
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.OPEN_DOCUMENTS_DELETION_DIALOG]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    ...mapActions('imanage', [POST_FILDERS_TO_IMANAGE]),
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    ...mapActions('documentsDownloadFiles', [OPEN_DOCUMENTS_DOWNLOAD_FILES]),
    ...mapMutations('documentsAISummary', ['SET_DOCUMENT_TO_SUMMARIZE_WITH_AI']),

    handleMenuAction (action) {
      switch (action) {
        case 'share':
          this.prepareSharePane()
          break
        case 'move':
          this.prepareMovePane()
          break
        case 'duplicate':
          this.prepareDuplicatePane()
          break
        case 'manageVersions':
          this.openVersioningPane('manage')
          break
        case 'showVersions':
          this.openVersioningPane('history')
          break
        case 'signingChecklistAdd':
          this.$router.push({
            name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
            state: {
              files: this.handledFiles,
            },
            query: {
              fileIds: this.handledFilesIds,
            },
          })
          break
        case 'delete':
          this.openDeleteAttachedFileDialog()
          break
        case 'rename':
          this.openRenameAttachedFileDialog()
          break
        case 'numbering':
          this.numberingDialogIsOpen = true
          break
        case 'summary':
          this.SET_DOCUMENT_TO_SUMMARIZE_WITH_AI(this.file)
          break
      }
    },
    versionDataLocator (action) {
      if (action === 'manageVersions') {
        return 'manage-versions-menu-item'
      } else if (action === 'showVersions') {
        return 'show-versions-menu-item'
      }
    },
    prepareSharePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'share',
        documents: this.handledFiles,
        checklistFilesParent: this.file.parent,
      })
    },
    prepareMovePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'move',
        documents: this.handledFiles,
        checklistFilesParent: this.file.parent,
      })
    },
    prepareDuplicatePane () {
      this.INIT_SELECTED_GROUPS(cloneDeep(this.file.rights.read.groups))
      this.INIT_SELECTED_MEMBERS(cloneDeep(this.file.rights.read.users))
      this.SET_TABLE_DOCUMENTS_PANE({
        isOpen: true,
        tab: 'duplicate',
        documents: this.handledFiles,
        checklistFilesParent: this.file.parent,
      })
    },
    openDeleteAttachedFileDialog () {
      this.OPEN_DOCUMENTS_DELETION_DIALOG(this.handledFiles)
    },
    openRenameAttachedFileDialog () {
      this.$store.commit(`checklist/${SET_RENAME_ATTACHED_FILE_DIALOG}`, {
        isOpen: true,
        fileToRename: this.file,
      })
    },
    openVersioningPane (tab) {
      this.SET_CHECKLIST_VERSIONING_PANE({ isOpen: true, documents: [this.file], tab })
    },
    updateDocumentsNumbering (file) {
      this.file.numbering = file.numbering
    },
    async polyOfficeOpenDialogHandler (file) {
      const selectedFileIds = this.onlyOneFile? [file.id] : this.handledFiles.map(attachedFile => attachedFile.id)
      const payload = new DocumentsExport(this.mnemo, selectedFileIds)
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.t('polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
    isOfficeEditionButtonShowed (document) {
      return this.isWopiEnabled && document.canEdit && this.isCurrentUserPm
    },
    async exportFilesToImanage (target) {
      try {
        await this.POST_FILDERS_TO_IMANAGE({
          mnemo: this.mnemo,
          data: {
            files: this.multiFiles? this.multiFiles.map(file => file.id) : [this.file.id],
            targetId: target.selected,
          },
        })
        toast.success(this.t('imanage.exportSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.t('imanage.exportFail'))
      }
    },
    onOpenDocument () {
     openFileViewer(this.mnemo, this.file.id)
    },
    fileLink (fileId) {
      return `${axios.defaults.baseURL}/room/${this.mnemo}/downloads/file/${fileId}`
    },
  },
}
</script>
<style scoped lang="scss">
:deep(.app-button.app-button-icon.btn-ellipsis) {
  width: 28px !important;
  height: 28px !important;
}
</style>
